import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { MediaContext } from './media-context';

type MediaProviderProps = {
  children: ReactNode;
};

export const MediaProvider: React.FC<MediaProviderProps> = ({ children }) => {
  const videoMapRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  // Off-screen container — browsers throttle decoders for detached video elements.
  const mediaContainerRef = useRef<HTMLDivElement | null>(null);

  const [pendingMediaCount, setPendingMediaCount] = useState(0);
  const isMediaLoading = pendingMediaCount > 0;

  const trackMediaLoad = useCallback(<T,>(promise: Promise<T>): Promise<T> => {
    setPendingMediaCount(c => c + 1);
    return promise.finally(() => setPendingMediaCount(c => Math.max(0, c - 1)));
  }, []);

  // Lazy audio graph, for the preview only: the export mixes its own audio from the model.
  const audioContextRef = useRef<AudioContext | null>(null);

  const ensureAudioGraph = useCallback(() => {
    if (audioContextRef.current) return audioContextRef.current;
    const Ctor =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audioContextRef.current = new Ctor();
    return audioContextRef.current;
  }, []);

  const resumeAudio = useCallback(async () => {
    const ctx = audioContextRef.current;
    if (ctx && ctx.state === 'suspended') await ctx.resume();
  }, []);

  const wireMediaSource = useCallback(
    (element: HTMLMediaElement) => {
      const wired = (element as { __audioWired?: boolean }).__audioWired;
      if (wired) return;
      const ctx = ensureAudioGraph();
      if (!ctx) return;
      try {
        const source = ctx.createMediaElementSource(element);
        source.connect(ctx.destination);
        // Keep the source node alive by tying it to the element: an unreferenced
        // MediaElementAudioSourceNode gets garbage-collected and the element goes silent.
        (element as { __audioWired?: boolean; __audioSource?: AudioNode }).__audioWired = true;
        (element as { __audioSource?: AudioNode }).__audioSource = source;
      } catch {
        // Already attached to another graph — ignore.
      }
    },
    [ensureAudioGraph]
  );

  const registerVideo = useCallback(
    (id: string, element: HTMLVideoElement) => {
      videoMapRef.current.set(id, element);
      if (mediaContainerRef.current && element.parentNode !== mediaContainerRef.current) {
        mediaContainerRef.current.appendChild(element);
      }
      wireMediaSource(element);
    },
    [wireMediaSource]
  );

  const unregisterVideo = useCallback((id: string) => {
    const el = videoMapRef.current.get(id);
    if (el && el.parentNode === mediaContainerRef.current) {
      mediaContainerRef.current?.removeChild(el);
    }
    videoMapRef.current.delete(id);
  }, []);

  const registerAudio = useCallback((element: HTMLAudioElement) => wireMediaSource(element), [wireMediaSource]);

  const getVideo = useCallback((id: string) => videoMapRef.current.get(id) ?? null, []);

  return (
    <>
      <div
        ref={mediaContainerRef}
        aria-hidden
        style={{ position: 'fixed', left: -99999, top: 0, width: 0, height: 0, overflow: 'hidden' }}
      />
      <MediaContext.Provider
        value={{
          getVideo,
          registerVideo,
          unregisterVideo,
          registerAudio,
          isMediaLoading,
          trackMediaLoad,
          resumeAudio
        }}
      >
        {children}
      </MediaContext.Provider>
    </>
  );
};
